import { getApiBaseUrl } from './axios';

const TOKEN_EXPIRY_BUFFER_MS = 30_000; // 30 seconds before actual expiry

/**
 * Return a valid access token for WebSocket reconnection.
 *
 * 1. Reads the current token from localStorage.
 * 2. Decodes the JWT `exp` claim (client-side only — server still validates).
 * 3. If the token is still valid (with a 30 s buffer), returns it as-is.
 * 4. If expired or about to expire, calls `/auth/refresh` with the stored
 *    refresh token and persists the new tokens in localStorage.
 * 5. Falls back to the stored token if anything goes wrong so the server
 *    can return a proper 4001 instead of the connection silently dying.
 */
export async function getFreshToken(): Promise<string> {
  const storedToken = localStorage.getItem('token') || '';
  if (!storedToken) return '';

  try {
    const parts = storedToken.split('.');
    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1]));
      const expiresAtMs = payload.exp * 1000;

      if (Date.now() < expiresAtMs - TOKEN_EXPIRY_BUFFER_MS) {
        return storedToken;
      }
    }
  } catch {
    return storedToken;
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return storedToken;

  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('refreshToken', data.refresh_token);
        }
        return data.access_token;
      }
    }
  } catch {
    // Refresh failed — fall through to stale token
  }

  return storedToken;
}
